import React from 'react';
import {Outlet, useNavigate, useLocation} from 'react-router-dom';
import {Box, Typography, Tooltip} from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import PaletteIcon from '@mui/icons-material/Palette';
import SettingsIcon from '@mui/icons-material/Settings';
import ExtensionIcon from '@mui/icons-material/Extension';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import LanguageIcon from '@mui/icons-material/Language';
import ScienceIcon from '@mui/icons-material/Science';
import {useOrg} from '../contexts/OrgContext';
import OrgSwitcher from './OrgSwitcher';
import ShellNavigation from './ShellNavigation';
import {colors} from '../theme/colors';

let buildVersion: {commitSha: string; version: string} | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  buildVersion = require('../generated/buildVersion').buildVersion;
} catch {
  // Not yet generated
}

const SIDEBAR_WIDTH = 240;

const navItems = [
  {label: 'Guides', icon: ListAltIcon, path: '/org/guides', enabled: true},
  {label: 'Document Groups', icon: FolderIcon, path: '/org/groups', enabled: true},
  {label: 'Source Library', icon: DescriptionIcon, path: '/org/sources', enabled: true},
  {label: 'Web Crawls', icon: LanguageIcon, path: '/org/crawls', enabled: true},
  {label: 'Background Jobs', icon: AutoAwesomeIcon, path: '/org/jobs', enabled: true},
  {label: 'Members', icon: PeopleIcon, path: '/org/members', enabled: false},
  {label: 'Branding', icon: PaletteIcon, path: '/org/branding', enabled: true},
  {label: 'Eval', icon: ScienceIcon, path: '/org/eval', enabled: true},
  {label: 'Integrations', icon: ExtensionIcon, path: '/org/integrations', enabled: true},
  {label: 'Settings', icon: SettingsIcon, path: '/org/settings', enabled: true},
];

export default function OrgAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {activeOrg} = useOrg();

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <Box sx={{minHeight: '100vh', display: 'flex', flexDirection: 'column'}}>
      {/* Shared top bar */}
      <ShellNavigation />

      {/* Body: sidebar + content */}
      <Box sx={{display: 'flex', flex: 1}}>
        {/* Sidebar */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid #E5E7EB',
            bgcolor: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Org switcher */}
          <Box sx={{px: 2, py: 2}}>
            <OrgSwitcher />
          </Box>

          {/* Nav items */}
          <Box sx={{px: 1, py: 0.5}}>
            {navItems.map(item => {
              const Icon = item.icon;
              const active = item.enabled && isActive(item.path);
              return (
                <Tooltip
                  key={item.label}
                  title={item.enabled ? '' : 'Coming soon'}
                  placement="right"
                >
                  <Box
                    onClick={item.enabled ? () => navigate(item.path) : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      mx: 0,
                      mb: 0.25,
                      borderRadius: '8px',
                      cursor: item.enabled ? 'pointer' : 'default',
                      bgcolor: active ? colors.blue[50] : 'transparent',
                      color: active
                        ? colors.blue[600]
                        : item.enabled
                          ? colors.gray[600]
                          : colors.gray[400],
                      fontWeight: active ? 600 : 400,
                      fontSize: 14,
                      '&:hover': item.enabled
                        ? {bgcolor: active ? colors.blue[50] : colors.gray[50]}
                        : {},
                    }}
                  >
                    <Icon sx={{fontSize: 20}} />
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        color: 'inherit',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* Spacer */}
          <Box sx={{mt: 'auto'}} />
        </Box>

        {/* Content area */}
        <Box sx={{flex: 1, bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column'}}>
          <Box sx={{flex: 1}}>
            <Outlet />
          </Box>
          {buildVersion && (
            <Box component="footer" sx={{py: 0.5, px: 2, textAlign: 'right'}}>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  '&:hover': {color: 'text.secondary'},
                }}
                onClick={() => navigate('/app/debug')}
              >
                v{buildVersion!.version} ({buildVersion!.commitSha})
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
